import { handleAxiosError } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { client } from '@/client';
import { Account, AccountAPIConfig } from '@/client/accounts';
import { APIToken } from '@/client/auth';
import { Actions, Subjects } from '@/client/users';
import {
  ACCOUNT_API_USE_LEGACY_INTERFACE,
  LoadingStatuses,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import { DialogContext } from '@/common/context';
import { TranslationFunctionType } from '@/common/types';
import { APITokenCard } from '@/components/accounts/APITokenCard';
import { AccountAPIForm } from '@/components/accounts/forms/AccountAPIForm';
import { useAccount } from '@/hooks/query';
import {
  useCreateLongLivedToken,
  useDeleteLongLivedToken,
  useLongLivedTokens,
  useUpdateLongLivedToken,
} from '@/hooks/query/long-lived-token.hooks';
import { useAppSelector } from '@/hooks/store';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { DialogType } from '@/system-settings/enums';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { ImportantIcon, isDemoRestrictedMode } from '@/utils/helpers';
import { AxiosError } from 'axios';
import { FormikValues } from 'formik';
import { InputSwitch, InputSwitchChangeEvent } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { MenuItem } from 'primereact/menuitem';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { AccountHeader } from './AccountHeader';

const StyledPasswordDialog = styled(FlexContainer)`
  flex-direction: column;
  align-items: flex-start;
  background-color: var(--yellow-opacity);
  border-radius: var(--medium-border-radius);
  padding: var(--default-padding) var(--big-padding);
  flex: 1 1 50%;
  margin-bottom: var(--medium-padding);

  p {
    margin: 0;
    font-size: var(--small-font-size);
    line-height: var(--small-line-height);
    margin-left: var(--small-padding);
  }
`;

const StyledEmptyState = styled(FlexContainer)`
  margin-top: var(--large-padding);

  i {
    color: var(--gray-darker);
    font-size: 48px;
  }

  h4 {
    margin-bottom: var(--xxsmall-padding);
  }

  p {
    color: var(--gray-darker);
    margin: 0;
  }
`;

const getPathItems = (
  account: Account | undefined,
  currentAccount: Account,
  can: (action: Actions, subject: Subjects) => boolean,
  t: TranslationFunctionType,
): MenuItem[] => {
  const pathItems: MenuItem[] = [];

  pathItems.push({
    label: currentAccount?.name,
    url: !currentAccount.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNT](currentAccount?.id)
      : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](currentAccount?.id),
    template: AppBreadCrumbTemplate,
  });

  if (
    currentAccount?.isSystem &&
    account &&
    can(Actions.READ, Subjects.ACCOUNTS)
  ) {
    pathItems.push(
      {
        label: t('accounts'),
        url: RedirectPaths[RedirectPathsEnum.ACCOUNTS](),
        template: AppBreadCrumbTemplate,
      },
      {
        label: account?.name,
        url: !currentAccount?.isSystem
          ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
          : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
        className: 'active',
        template: AppBreadCrumbTemplate,
      },
    );
  }

  pathItems.push({
    label: t('api'),
    url: currentAccount?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNTS_API](
          account ? account?.id : currentAccount?.id,
        )
      : RedirectPaths[RedirectPathsEnum.ACCOUNT_API](),
    template: AppBreadCrumbTemplate,
  });

  return pathItems;
};

export const AccountAPIPage: React.FC = () => {
  const { t } = useTranslation();
  const { can } = usePermission();
  const toast = useToast();
  const { id: accountId } = useParams();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const { account } = useAccount({
    accountId: currentAccount?.isSystem ? accountId : currentAccount?.id,
  });
  const { setDialogData } = useContext(DialogContext);

  const user = useAppSelector(selectCurrentUser);

  const isDemoAccount = isDemoRestrictedMode(currentAccount, user);

  const currentAccountId =
    currentAccount?.isSystem && accountId ? accountId : currentAccount?.id;

  const { longLivedTokens, isLoading: isLoadingTokens } = useLongLivedTokens({
    accountId: currentAccountId || '',
    enabled: !!currentAccountId,
  });
  const { create: createToken, isLoading: isCreatingToken } =
    useCreateLongLivedToken();
  const { deleteToken } = useDeleteLongLivedToken();
  const { update: updateToken } = useUpdateLongLivedToken();

  const [showOldInterface, setShowOldInterface] = useState(() => {
    const stored = localStorage.getItem(ACCOUNT_API_USE_LEGACY_INTERFACE);
    return stored ? JSON.parse(stored) : false;
  });
  const [newTokenName, setNewTokenName] = useState('');
  const [tokens, setTokens] = useState<APIToken[]>([]);

  // Original state (for old interface)
  const [initialValues, setInitialValues] = useState<AccountAPIConfig>({
    active: false,
    username: '',
    password: '',
    clientId: '',
    clientSecret: '',
    authenticationUrl: '',
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    localStorage.setItem(
      ACCOUNT_API_USE_LEGACY_INTERFACE,
      JSON.stringify(showOldInterface),
    );
  }, [showOldInterface]);

  useEffect(() => {
    const currentAccountId =
      currentAccount?.isSystem && accountId ? accountId : currentAccount?.id;

    if (currentAccountId) {
      getAccountAPIClient(currentAccountId);
    }
  }, [accountId, currentAccount]);

  useEffect(() => {
    if (longLivedTokens) {
      setTokens(longLivedTokens.result);
    }
  }, [longLivedTokens]);

  const getAccountAPIClient = async (accountId: string) => {
    try {
      const response = await client.accounts.getAccountAPIClient(accountId);
      setInitialValues(response);
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const handleUpdateAccountAPIClient = async (
    data: FormikValues,
    regenerate?: boolean,
  ) => {
    const currentAccountId =
      currentAccount?.isSystem && accountId ? accountId : currentAccount?.id;

    if (!currentAccountId) return;

    const payload = regenerate
      ? { active: initialValues.active, regenerate: true }
      : {
          active: data.active,
          regenerate: false,
        };

    try {
      setIsSubmitting(true);

      const response = await client.accounts.updateAccountAPIClient(
        currentAccountId,
        payload,
      );

      setInitialValues({
        ...response,
        active: data.active,
      });

      if (!regenerate) {
        toast?.success(
          t('toast.success'),
          data.active ? t('api.enable.success') : t('api.disable.success'),
        );
      } else {
        toast?.success(
          t('toast.success'),
          t('api.credentials.regenerate.success'),
        );
      }
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateLongLivedToken = async () => {
    if (!currentAccount) return;

    try {
      const response = await createToken(currentAccount.id, {
        accountId: currentAccount.id,
        name: newTokenName,
      });

      const newToken: APIToken = {
        id: Date.now().toString(),
        token: response.token,
        name:
          response.name ||
          t('api.tokens.fallback.name', { index: tokens.length + 1 }),
        isNew: true,
      };

      setTokens([newToken, ...(tokens || [])]);
      setNewTokenName('');
      toast?.success(t('toast.success'), t('api.tokens.generate.success'));
    } catch (error) {
      handleAxiosError(error as Error | AxiosError, toast);
    }
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast?.success(t('toast.success'), t('api.tokens.copy.success'));
    } catch (err) {
      toast?.error(t('toast.error'), t('api.tokens.copy.error'));
    }
  };

  const handleDeleteToken = async (tokenId: string) => {
    if (!currentAccountId) return;

    try {
      await deleteToken(currentAccountId, tokenId);
      setTokens(tokens.filter((token) => token.id !== tokenId));
      toast?.success(t('toast.success'), t('api.tokens.delete.success'));
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const handleUpdateToken = async (tokenId: string, newName: string) => {
    if (!currentAccountId) return;

    try {
      await updateToken(currentAccountId, tokenId, { name: newName });
      toast?.success(t('toast.success'), t('api.tokens.update.success'));
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const PasswordDialog = () => (
    <StyledPasswordDialog>
      <FlexContainer justify="flex-start">
        <ImportantIcon />
        <p>{t('api.user.password.warning')}</p>
      </FlexContainer>
    </StyledPasswordDialog>
  );

  return (
    <>
      {!currentAccount || !account ? (
        <FlexContainer direction="column" className="mt-5">
          <ProgressSpinner />
        </FlexContainer>
      ) : (
        <FlexContainer direction="column" align="flex-start">
          <div className="w-full">
            <AppBreadCrumb
              model={getPathItems(
                account as Account,
                currentAccount as Account,
                can,
                t,
              )}
            />
            <AccountHeader selectedAccount={account} />

            <div style={{ marginBottom: 'var(--large-padding)' }}>
              <h3>{t('api.configuration')}</h3>
              <PasswordDialog />
              <FlexContainer justify="space-between">
                <h4>{t('api.enable')}</h4>
                <InputSwitch
                  inputId="active"
                  name="active"
                  checked={initialValues?.active}
                  disabled={isSubmitting}
                  onChange={(e: InputSwitchChangeEvent) =>
                    isDemoAccount
                      ? undefined
                      : setDialogData({
                          show: true,
                          type: DialogType.CONFIRMATION,
                          header: e.target.value
                            ? t('api.confirm.enable')
                            : t('api.confirm.disable'),
                          message: e.target.value
                            ? t('api.confirm.enable.message')
                            : t('api.confirm.disable.message'),
                          onAccept: () =>
                            handleUpdateAccountAPIClient({
                              active: e.target.value,
                            }),
                        })
                  }
                />
              </FlexContainer>
              <a
                href="javascript:void(0)"
                onClick={() => setShowOldInterface(!showOldInterface)}
              >
                {showOldInterface
                  ? t('api.enable.oldInterface')
                  : t('api.enable.newInterface')}
              </a>
            </div>

            {showOldInterface ? (
              <AccountAPIForm
                accountId={
                  currentAccount?.isSystem && accountId
                    ? accountId
                    : currentAccount?.id
                }
                initialValues={initialValues}
                state={
                  isSubmitting ? LoadingStatuses.LOADING : LoadingStatuses.IDLE
                }
                disabled={isSubmitting}
                onSubmit={handleUpdateAccountAPIClient}
                onRegenerate={() =>
                  handleUpdateAccountAPIClient(initialValues, true)
                }
              />
            ) : (
              // New Token Management Interface
              <div>
                <div className="mt-2">
                  <h4>{t('api.tokens')}</h4>
                  <p>{t('api.tokens.description')}</p>

                  <FlexContainer justify="space-between" gap={24}>
                    <InputText
                      value={newTokenName}
                      onChange={(e) => setNewTokenName(e.target.value)}
                      placeholder={t('api.tokens.name.placeholder')}
                      className="w-full"
                      disabled={isCreatingToken || isDemoAccount}
                    />
                    <AppButton
                      onClick={handleCreateLongLivedToken}
                      className="flex items-center gap-2"
                      isDisabled={!initialValues.active || isDemoAccount}
                      icon="pi pi-plus"
                      label={t('api.tokens.generate')}
                      state={
                        isCreatingToken
                          ? LoadingStatuses.LOADING
                          : LoadingStatuses.IDLE
                      }
                    />
                  </FlexContainer>

                  {isLoadingTokens ? (
                    <FlexContainer
                      direction="column"
                      align="center"
                      justify="center"
                      className="mt-4"
                    >
                      <ProgressSpinner />
                      <p>{t('api.tokens.loading')}</p>
                    </FlexContainer>
                  ) : !tokens || tokens.length === 0 ? (
                    <StyledEmptyState
                      direction="column"
                      align="center"
                      justify="center"
                    >
                      <i className="pi pi-key" />
                      <h4>{t('api.tokens.empty.title')}</h4>
                      <p>{t('api.tokens.empty.description')}</p>
                    </StyledEmptyState>
                  ) : (
                    <div>
                      {tokens.map((token) => (
                        <APITokenCard
                          key={token.id}
                          token={token}
                          onCopy={(text) => handleCopy(text)}
                          onDelete={() => {
                            isDemoAccount
                              ? undefined
                              : setDialogData({
                                  show: true,
                                  type: DialogType.CONFIRMATION,
                                  header: t('api.tokens.delete.title'),
                                  message: t('api.tokens.delete.message'),
                                  onAccept: () => handleDeleteToken(token.id),
                                });
                          }}
                          onUpdate={handleUpdateToken}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </FlexContainer>
      )}
    </>
  );
};
